/* -------------------------- Design imports start -------------------------- */
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import { Typography, MenuItem, Menu } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import Cookies from 'js-cookie'
import React, { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import LanguageSelectionDrawer from './LanguageSelectionDrawer'
import useMobileService from '../../hooks/useMobileService'
import LogTool from '../../logger/logTools'
import { type User } from '../../utils/types'
import { useWebSocket } from '../../WebSocketProvider'
import Button from '../Button'
import { useSelfUser } from '../../features/employee/hooks/useEmployeeQueries'
import { useLogout } from '../../api/hooks/useAuthMutations'
import { isFeatureEnabled } from "../../config/featureFlags";
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  setUser: (user: User | undefined) => void
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function AccountButton(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const { setUser } = props
  const isMobile = useMobileService()
  let size
  isMobile
    ? (size = { height: '27vh', width: '95vw' })
    : (size = { height: '250px', width: '500px' })
  const log = new LogTool({ context: 'AccountButton', enable: true, logLevel: 'warn' })
  const { setIsLoggedIn, setAccessTokenWS } = useWebSocket()
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const id = anchorEl ? 'simple-popover' : undefined
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const history = useHistory()
  const { t } = useTranslation(['common'])
  const { data: userData, isError, error } = useSelfUser(['roles'])
  const logoutMutation = useLogout()
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (isError && error) {
      log.error('Error fetching user', error)
    }
  }, [isError, error])
  /* ------------------------------- Effects end ------------------------------ */
  /* ------------------------- Utility functions start ------------------------ */
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleLogout = async (): Promise<void> => {
    try {
      await logoutMutation.mutateAsync()
    } catch (logoutError) {
      log.error('Error while logging out', logoutError)
    } finally {
      setUser(undefined)
      setIsLoggedIn(false)
      setAccessTokenWS('')
      Cookies.remove('access')
      Cookies.remove('refresh')
      history.push('/signIn')
      handleClose() // Close the Popover after handling logout
    }
  }

  const handleProfileClick = (): void => {
    handleClose() // Close the Popover when the profile is clicked
    history.push('/profile')
  }

  const handleSettingsClick = (): void => {
    setModalOpen(true)
    handleClose() // Close the Popover when the settings are clicked
  }

  /* ------------------------- Callback functions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <Button
        id="accountButton"
        style={{ marginLeft: '25px', color: 'inherit' }}
        startIcon={<AccountCircleOutlinedIcon />}
        size="medium"
        variant="text"
        color="inherit"
        onClick={handleClick}
      >
        <Typography style={{ fontSize: '13px', marginLeft: '5px', textTransform: 'none' }}>
          {userData?.isGuestUser ? t('common:content.label.guestUser') : userData?.email}
        </Typography>
      </Button>
      {anchorEl && (
        <Menu
          open={Boolean(anchorEl)}
          id={id}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          style={{ marginTop: '20px' }}
        >
          <MenuItem
            id="settingsMenuItem"
            onClick={handleSettingsClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <SettingsIcon style={{ color: 'darkgray' }} />
            <Typography variant="inherit" sx={{ marginLeft: 1, fontWeight: 'bold' }}>
              {t('common:content.label.settings')}
            </Typography>
          </MenuItem>
          {(!userData?.isGuestUser && isFeatureEnabled('profile')) && (
            <MenuItem
              id="profileMenuItem"
              onClick={handleProfileClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <AccountCircleOutlinedIcon style={{ color: 'darkgray' }} />
              <Typography variant="inherit" sx={{ marginLeft: 1, fontWeight: 'bold' }}>
                {t('common:content.label.profile')}
              </Typography>
            </MenuItem>
          )}
          <MenuItem
            id="logoutMenuItem"
            onClick={() => {
              void handleLogout()
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <LogoutIcon style={{ color: 'darkgray' }} />
            <Typography variant="inherit" sx={{ marginLeft: 1, fontWeight: 'bold' }}>
              {t('common:content.label.logout')}
            </Typography>
          </MenuItem>
        </Menu>
      )}
      <LanguageSelectionDrawer open={modalOpen} setOpen={setModalOpen} />
    </>
  )
}
